import { useEffect, useRef } from 'react';
import { renderToString } from 'react-dom/server';
import L from 'leaflet';
import type { GeolocationPosition } from '@inloco/shared';
import type { RoomSession } from '../../../types/room-session';
import { VisitorMarker } from '../Markers';
import type { TFunction } from 'i18next';
import { buildUserMarkerPopupContent } from '@/utils/constants';

interface UseUserMarkerProps {
    mapRef: React.RefObject<L.Map | null>;
    isMapReady: boolean;
    userPosition?: GeolocationPosition | null;
    roomSession: RoomSession | null;
    primaryColor?: string;
    secondaryColor?: string;
    t: TFunction;
    language: string;
}

/**
 * Smoothly animates marker movement from one position to another
 */
function animateMarkerTo(
    marker: L.Marker,
    newLatLng: L.LatLngExpression,
    duration: number = 1000
) {
    const startLatLng = marker.getLatLng();
    const endLatLng = L.latLng(newLatLng);
    const startTime = Date.now();

    function animate() {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease-out cubic) for more natural movement
        const easedProgress = 1 - Math.pow(1 - progress, 3);

        // Interpolate coordinates
        const lat = startLatLng.lat + (endLatLng.lat - startLatLng.lat) * easedProgress;
        const lng = startLatLng.lng + (endLatLng.lng - startLatLng.lng) * easedProgress;

        marker.setLatLng([lat, lng]);

        if (progress < 1) {
            requestAnimationFrame(animate);
        }
    }

    animate();
}

export function useUserMarker({
    mapRef,
    isMapReady,
    userPosition,
    roomSession,
    t,
    language,
    primaryColor = '#00d492',
    secondaryColor = '#00bc7d'
}: UseUserMarkerProps) {
    const userMarkerRef = useRef<L.Marker | null>(null);
    const accuracyCircleRef = useRef<L.Circle | null>(null);
    const hasAutoCenteredRef = useRef(false);

    // Cleanup: clean ref when map is destroyed
    useEffect(() => {
        return () => {
            userMarkerRef.current = null;
            accuracyCircleRef.current = null;
        };
    }, []);

    // Current user marker
    useEffect(() => {
        if (!isMapReady || !mapRef.current || !userPosition) {
            if (userMarkerRef.current) {
                userMarkerRef.current.remove();
                userMarkerRef.current = null;
            }
            if (accuracyCircleRef.current) {
                accuracyCircleRef.current.remove();
                accuracyCircleRef.current = null;
            }
            return;
        }

        const { lt, lg } = userPosition;

        const { ac } = userPosition;

        const divIconOptions: L.DivIconOptions = {
            className: 'custom-marker',
            html: renderToString(<VisitorMarker
                //text={t('map.you')}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
            />),
            iconSize: [32, 32],
            iconAnchor: [16, 16],
            popupAnchor: [0, -10]
        }

        // Fallback se il GPS non restituisce un valore valido
        const isValidAccuracy = Number.isFinite(ac) && ac > 0;
        const rawAccuracy = isValidAccuracy ? ac : null;
        const effectiveAccuracy = rawAccuracy ? Math.max(rawAccuracy, 5) : 15; // 15 come fallback grafico standard

        const popupContent = buildUserMarkerPopupContent(t, rawAccuracy, effectiveAccuracy);

        // Auto-center only once when the first valid user position is available.
        if (!hasAutoCenteredRef.current) {
            mapRef.current.setView([lt, lg], mapRef.current.getZoom());
            hasAutoCenteredRef.current = true;
        }

        if (userMarkerRef.current) {
            // Animate marker movement to new position
            animateMarkerTo(userMarkerRef.current, [lt, lg], 1000);

            // Update icon to reflect current translation
            const updatedIcon = L.divIcon({ ...divIconOptions });
            userMarkerRef.current.setIcon(updatedIcon);
            userMarkerRef.current.setPopupContent(popupContent);

            // Update accuracy circle
            if (accuracyCircleRef.current) {
                accuracyCircleRef.current.setLatLng([lt, lg]);
                accuracyCircleRef.current.setRadius(ac);
            }
        } else {
            // Create new user marker
            const userIcon = L.divIcon({ ...divIconOptions });

            const marker = L.marker([lt, lg], { icon: userIcon }).addTo(mapRef.current);

            marker.bindPopup(popupContent);
            userMarkerRef.current = marker;

            // Create accuracy circle
            const circle = L.circle([lt, lg], {
                radius: ac < 5 ? 5 : ac,
                color: primaryColor,
                fillColor: primaryColor,
                fillOpacity: 0.2,
                weight: 1,
            }).addTo(mapRef.current);

            accuracyCircleRef.current = circle;
        }
        // Trigger popup/icon refresh when language changes.
    }, [isMapReady, userPosition, roomSession, primaryColor, secondaryColor, language, mapRef, t]);
}
